import React from 'react'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { 
  CalendarDays, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  MapPin, 
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'

export interface ControlBarProps {
  location: string
  setLocation: (val: string) => void
  period: string
  setPeriod: (val: string) => void
  spaceType: string[]
  setSpaceType: (val: string[]) => void
  segment: string
  setSegment: (val: string) => void
  groupBy: string
  setGroupBy: (val: string) => void
  includeFuel: boolean
  setIncludeFuel: (val: boolean) => void
}

export const YieldControlBar: React.FC<ControlBarProps> = ({
  location, setLocation,
  period, setPeriod,
  spaceType, setSpaceType,
  segment, setSegment,
  groupBy, setGroupBy,
  includeFuel, setIncludeFuel
}) => {
  
  const toggleSpaceType = (type: string) => {
    if (spaceType.includes(type)) {
      setSpaceType(spaceType.filter(t => t !== type))
    } else {
      setSpaceType([...spaceType, type])
    }
  }

  return (
    <div className="border-b bg-white p-4 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between sticky top-0 z-10 shadow-sm">
      
      {/* Left Group: Location & Period */}
      <div className="flex flex-wrap items-center gap-4">
        
        {/* Location */}
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-slate-500" />
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="phx">Phoenix Sky Harbor</SelectItem>
              <SelectItem value="sco">Scottsdale Airpark</SelectItem>
              <SelectItem value="all">All Locations</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Separator */}
        <div className="h-4 w-px bg-slate-200 hidden md:block" />

        {/* Period Selector */}
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-500" />
          <div className="flex items-center bg-slate-100 rounded-md p-1">
             <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" onClick={() => setPeriod('2025-12')}>
                <ChevronLeft className="w-3 h-3" />
             </Button>
             <span className="text-sm font-medium px-2 min-w-[100px] text-center">
                {period === '2026-01' ? 'Jan 2026' : period}
             </span>
             <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" onClick={() => setPeriod('2026-02')}>
                <ChevronRight className="w-3 h-3" />
             </Button>
          </div>
        </div>

         {/* Separator */}
         <div className="h-4 w-px bg-slate-200 hidden md:block" />

        {/* Space Type Chips */}
        <div className="flex items-center gap-2">
           <span className="text-sm text-slate-500 font-medium hidden lg:inline">Space:</span>
           <div className="flex gap-1">
              {['Hangar', 'Community', 'Tiedown'].map(type => (
                <Badge 
                  key={type}
                  variant={spaceType.includes(type) ? 'default' : 'outline'}
                  className={`cursor-pointer ${spaceType.includes(type) ? 'bg-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                  onClick={() => toggleSpaceType(type)}
                >
                  {type}
                </Badge>
              ))}
              <Badge 
                  variant={spaceType.includes('Ramp') ? 'default' : 'outline'}
                  className={`cursor-pointer ${spaceType.includes('Ramp') ? 'bg-slate-900' : 'text-slate-400 hover:text-slate-600 border-dashed'}`}
                  onClick={() => toggleSpaceType('Ramp')}
                >
                  Ramp
              </Badge>
           </div>
        </div>

      </div>

      {/* Right Group: Segment, Group By, Export */}
      <div className="flex flex-wrap items-center gap-4">
        
        {/* Revenue Segment */}
        <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 font-medium">Segment:</span>
            <Select value={segment} onValueChange={setSegment}>
                <SelectTrigger className="w-[130px] h-9">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All Segments</SelectItem>
                    <SelectItem value="lease">Lease Only</SelectItem>
                    <SelectItem value="transient">Transient Only</SelectItem>
                    <SelectItem value="idle">Idle Only</SelectItem>
                </SelectContent>
            </Select>
        </div>

        {/* Group By */}
        <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 font-medium">Group:</span>
            <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-[130px] h-9">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="building">Building</SelectItem>
                    <SelectItem value="type">Unit Type</SelectItem>
                    <SelectItem value="segment">Rev. Segment</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                </SelectContent>
            </Select>
        </div>

        {/* Include Fuel Toggle */}
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-md">
            <Switch 
                id="include-fuel" 
                checked={includeFuel} 
                onCheckedChange={setIncludeFuel} 
            />
            <Label htmlFor="include-fuel" className="text-sm font-medium cursor-pointer">
                Include Fuel
            </Label>
        </div>

        <Button variant="outline" size="sm" className="h-9">
            <Download className="w-4 h-4 mr-2" />
            Export
        </Button>

      </div>
    </div>
  )
}
